import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import { renderNameWithColor } from "../../../Utils/colors";
import {
  convertUTCToDateInvitation,
  convertUTCToTime12Hour,
} from "../../../Utils/dateUtils";
import SpecialInstructionsDisplay from "./SpecialInstructionsDisplay";
import { isFuneralEvent, FuneralHostLine } from "./funeralFlyer";

const Invitation = ({
  search,
  openAddEditModal,
  openDeleteModal,
  events,
  loading,
  isArchived = false,
  isDraft = false,
  selectedIds = [],
  onToggleSelect = () => {},
  cardVariant = "my-invitations",
}) => {
  const navigate = useNavigate();
  const scrollableRefs = useRef({});

  const formatMemberName = (host) => {
    let firstName = host.first_name || (host.name ? host.name.trim().split(" ")[0] : "");
    if (!firstName) firstName = "Unnamed";
    const suffix = host.suffix || "";
    const specialName = host.special_name || "";

    if (suffix) {
      const suffixUpper = suffix.trim().toUpperCase();
      const suffixWithPeriod =
        suffixUpper === "SR" || suffixUpper === "JR" ? `${suffix.trim()}.` : suffix.trim();
      return `${firstName}, ${suffixWithPeriod}`;
    }
    if (specialName) {
      return `${firstName} ${specialName}`;
    }
    return firstName;
  };

  const formatHostName = (event) => {
    if (event?.Host) return formatMemberName(event.Host);
    if (event?.host_name) {
      return event.host_name.split(" ")[0] || event.host_name;
    }
    return "-";
  };

  // Format backend date/time values using UTC (no local timezone conversion)
  const formatDateShort = convertUTCToDateInvitation;
  const formatTime12Hour = convertUTCToTime12Hour;

  // Helper function to format date range if both start and end dates exist
  const formatDateRange = (startDate, endDate, fallbackDate) => {
    const start = startDate || fallbackDate;
    const end = endDate;
    
    if (!start) return "-";
    
    const startFormatted = formatDateShort(start);
    if (!end) return startFormatted;
    
    // Check if dates are the same by comparing timestamps (ignoring time)
    const startTimestamp = Number(start);
    const endTimestamp = Number(end);
    if (!isNaN(startTimestamp) && !isNaN(endTimestamp)) {
      const startDateOnly = new Date(startTimestamp);
      const endDateOnly = new Date(endTimestamp);
      startDateOnly.setUTCHours(0, 0, 0, 0);
      endDateOnly.setUTCHours(0, 0, 0, 0);
      
      if (startDateOnly.getTime() === endDateOnly.getTime()) {
        // If same date, just show once
        return startFormatted;
      }
    }
    
    const endFormatted = formatDateShort(end);
    return `${startFormatted} - ${endFormatted}`;
  };

  // Handle scroll to detect when user reaches bottom
  const handleScroll = (eventId, element) => {
    const { scrollTop, scrollHeight, clientHeight } = element;
    const isAtBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 5; // 5px threshold
    
    if (isAtBottom) {
      element.classList.add('scrolled-to-bottom');
    } else {
      element.classList.remove('scrolled-to-bottom');
    }
  };

  // Set up scroll listeners for each event card
  useEffect(() => {
    const scrollElements = Object.values(scrollableRefs.current);
    const cleanupFunctions = [];
    
    scrollElements.forEach((element) => {
      if (element) {
        // Check initial state
        handleScroll(null, element);
        
        // Add scroll listener
        const scrollHandler = () => handleScroll(null, element);
        element.addEventListener('scroll', scrollHandler);
        
        cleanupFunctions.push(() => {
          element.removeEventListener('scroll', scrollHandler);
        });
      }
    });
    
    // Cleanup all listeners
    return () => {
      cleanupFunctions.forEach(cleanup => cleanup());
    };
  }, [events]);

  // Loading state
  if (loading) {
    return (
      <div className="events-content">
        <div className="text-center">
          <CircularProgress size={30} color="inherit" />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="events-content">
        <div className="events-scroll-area">
        {events && events.length > 0 ? (
          <div className="event-grid">
            {events.map((userEvent) => (
              <div className={`event-card event-card-${cardVariant}`} key={userEvent.id || userEvent.title}>
                <div
                  className="event-card-image-wrapper"
                  style={{
                    position: "relative",
                    flex: "0 0 48%",
                    minHeight: "48%",
                    width: "100%",
                    overflow: "hidden",
                  }}
                >
                  {isArchived && (
                    <div
                      className="cursor-pointer"
                      style={{
                        position: "absolute",
                        top: "12px",
                        left: "12px",
                        zIndex: 1,
                      }}
                    >
                      <input
                        type="checkbox"
                        style={{ width: "16px", height: "16px", display: "block" }}
                        checked={selectedIds.includes(userEvent.id)}
                        onChange={() => onToggleSelect(userEvent.id)}
                      />
                    </div>
                  )}
                  <img
                    src={
                      userEvent.image ||
                      window.getPublicUrl("/assets/images/card-5.svg")
                    }
                    className="event-img event-img-in-wrapper"
                    alt={userEvent.title || "Event"}
                  />
                </div>
                <div className="card-body" style={{ flexDirection: "column" }}>
                  <div 
                    style={{ display: "flex", gap: "8px", justifyContent: "flex-start", flex: "1", flexDirection: "column", alignItems: "stretch", width: "100%", minHeight: "0" }}
                    ref={(el) => {
                      if (el && userEvent.id) {
                        scrollableRefs.current[userEvent.id] = el;
                      }
                    }}
                    onScroll={(e) => {
                      if (userEvent.id) {
                        handleScroll(userEvent.id, e.target);
                      }
                    }}
                  >
                    <div className={`event-details-formatted${isFuneralEvent(userEvent) ? " funeral-flyer" : ""}`} style={{ flex: 1, minWidth: 0 }}>
                      {isFuneralEvent(userEvent) ? (
                        <FuneralHostLine event={userEvent} formatName={formatMemberName} />
                      ) : (
                        <>
                          {(userEvent.Host || userEvent.host_name) && (
                            <div className="event-detail-row">
                              <span className="event-detail-label">Host:</span>
                              <span className="event-detail-value">
                                {formatHostName(userEvent)}
                              </span>
                            </div>
                          )}
                          {userEvent?.CoHost && (
                            <div className="event-detail-row">
                              <span className="event-detail-label">Co-Host:</span>
                              <span className="event-detail-value">
                                {formatMemberName(userEvent.CoHost)}
                              </span>
                            </div>
                          )}
                          <div className="event-detail-row">
                            <span className="event-detail-label">Line Color:</span>
                            <span className="event-detail-value">
                              {(() => {
                                const hex =
                                  userEvent?.Host?.hex_code ||
                                  userEvent?.host_hex_code ||
                                  userEvent?.hex_code ||
                                  "#000000";
                                const lineColorName =
                                  userEvent?.Host?.color_name ||
                                  userEvent?.host_color_name ||
                                  userEvent?.color_name ||
                                  "";

                                if (!lineColorName) return "-";

                                return (
                                  <span style={{ color: hex }}>
                                    {lineColorName}
                                  </span>
                                );
                              })()}
                            </span>
                          </div>
                        </>
                      )}
                      <div className="event-detail-row">
                        <span className="event-detail-label">Name of Event:</span>
                        <span className="event-detail-value">{userEvent.title || "-"}</span>
                      </div>
                      <div className="event-detail-row">
                        <span className="event-detail-label">Event Date:</span>
                        <span className="event-detail-value">
                          {formatDateRange(userEvent.start_date, userEvent.end_date, userEvent.date)}
                        </span>
                      </div>
                      <div className="event-detail-row">
                        <span className="event-detail-label">Time:</span>
                        <span className="event-detail-value">
                          {formatTime12Hour(userEvent.event_time || userEvent.time) || "-"}
                        </span>
                      </div>
                      {userEvent.venue && (
                        <div className="event-detail-row">
                          <span className="event-detail-label">Venue:</span>
                          <span className="event-detail-value">{userEvent.venue}</span>
                        </div>
                      )}
                      <div className="event-detail-row">
                        <span className="event-detail-label">Location:</span>
                        <span className="event-detail-value  location-value">
                          {userEvent.state && userEvent.location ? (
                            <>
                              <div>{userEvent.location}</div>
                              <div>{userEvent.state}</div>
                            </>
                          ) : (
                            userEvent.location || userEvent.state || "-"
                          )}
                        </span>
                      </div>
                      {userEvent.document && (
                        <div className="event-detail-row">
                          <span className="event-detail-label">View Add'l Details:</span>
                          <span className="event-detail-value">
                            <a
                              href={userEvent.document}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: "#1890ff", textDecoration: "underline", wordBreak: "break-all" }}
                            >
                              View PDF
                            </a>
                          </span>
                        </div>
                      )}
                      <SpecialInstructionsDisplay text={userEvent.description} />
                      {userEvent.Rsvp_deadline && (
                        <div className="event-detail-row">
                          <span className="event-detail-label">RSVP Deadline:</span>
                          <span className="event-detail-value">
                            {isFuneralEvent(userEvent) && "RSVP by "}
                            {formatDateShort(userEvent.Rsvp_deadline)}
                          </span>
                        </div>
                      )}
                      <div
                        className={`view-icon invitation-card-actions${
                          userEvent?.status === 2
                            ? " invitation-card-actions--with-cancelled"
                            : ""
                        }${
                          isArchived && userEvent?.status === 2
                            ? " invitation-card-actions--archived-cancelled"
                            : ""
                        }`}
                      >
                        {userEvent?.status === 2 && (
                          <span className="event-cancelled-label">EVENT CANCELLED</span>
                        )}
                        {/* Edit */}
                        <div className="cursor-pointer icon-tooltip-wrapper">
                          <span
                            className="icon-btn"
                            onClick={() => openAddEditModal("Edit", userEvent)}
                          >
                            <img
                              src={window.getPublicUrl(
                                "/assets/images/edit-icon.svg"
                              )}
                              alt="Edit"
                            />
                          </span>
                          <div className="icon-tooltip">Edit event</div>
                        </div>
                        {/* View */}
                        <div className="cursor-pointer icon-tooltip-wrapper">
                          <span
                            className="icon-btn"
                            onClick={() =>
                              navigate("/events/all-tree-events", {
                                state: {
                                  eventsListType: isArchived ? 2 : isDraft ? 3 : 0,
                                  ...(isArchived && events?.length ? { archivedEvents: events } : {}),
                                },
                              })
                            }
                          >
                            <img
                              src={window.getPublicUrl(
                                "/assets/images/eye-icon.svg"
                              )}
                              alt="View"
                            />
                          </span>
                          <div className="icon-tooltip">View Event Status</div>
                        </div>
                        {/* Cancel (My Invitations and Draft: cancel, not delete) */}
                        {((!isArchived && !isDraft && userEvent?.status !== 2) || isDraft) && (
                          <div className="cursor-pointer icon-tooltip-wrapper">
                            <span
                              className="icon-btn"
                              onClick={() => openDeleteModal(userEvent, "cancel")}
                            >
                              <img
                                src={window.getPublicUrl(
                                  "/assets/images/cancel.svg"
                                )}
                                alt="Cancel"
                              />
                            </span>
                            <div className="icon-tooltip">
                              {isDraft ? "Cancel draft" : "Cancel event"}
                            </div>
                          </div>
                        )}
                        {/* Delete (only in archived invitations) */}
                        {isArchived && (
                          <div className="cursor-pointer icon-tooltip-wrapper">
                            <span
                              className="icon-btn"
                              onClick={() => openDeleteModal(userEvent, "delete")}
                            >
                              <img
                                src={window.getPublicUrl(
                                  "/assets/images/deleteEvent.svg"
                                )}
                                alt="Delete"
                              />
                            </span>
                            <div className="icon-tooltip">Delete event</div>
                          </div>
                        )}
                      </div>
                      <div className="event-detail-row event-invitation-status-section">
                        <div className="event-invitation-status-title">
                          {isFuneralEvent(userEvent) ? "Notice" : "Invitation Status"}
                        </div>
                        <div className="event-invitation-status-counts">
                          {isFuneralEvent(userEvent) ? (
                            <>
                              <strong>{userEvent.total_invited ?? 0}</strong> Notice(s) Sent
                            </>
                          ) : (
                            <>
                              <strong>{userEvent.total_invited ?? 0}</strong> Invited<span className="event-invitation-status-divider">|</span><strong>{userEvent.accepted_count ?? 0}</strong> Accepted<span className="event-invitation-status-divider">|</span><strong>{userEvent.declined_count ?? 0}</strong> Declined<span className="event-invitation-status-divider">|</span><strong>{userEvent.pending_count ?? 0}</strong> Pending
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div style={{ marginTop: "0", paddingTop: "16px", borderTop: "1px solid #e0e0e0", width: "100%" }}>
                    <p style={{ color: "#808080", fontSize: "12px", margin: "0", lineHeight: "1", textAlign: "center" }}>
                      Flyer auto-archives the day after the event and can be reused for another event anytime.
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            {search && search !== "" ? (
              <div className="empty-events-state text-center">
                <p className="empty-events-text mb-2">
                  No events found matching "<b>{search}</b>"
                </p>
              </div>
            ) : ( 
              <div className="empty-events-state text-center">
                <img
                  src={window.getPublicUrl("/assets/images/event-bg-img.png")}
                  alt="No events"
                  className="empty-events-img mx-auto mb-4"
                />
                {isArchived ? (
                  <>
                    <p className="empty-events-text mb-4">
                      You have not created any archived invitations yet.
                    </p>
                    <p className="empty-events-text mb-4">
                    Heads up!!!!! Past events can be reused under this tab by clicking on the edit button.
                    </p>
                  </>
                ) : isDraft ? (
                  <>
                    <p className="empty-events-text mb-4">
                      You have not saved an invitation yet.
                    </p>
                    <p className="empty-events-text mb-4">
                      Use &quot;Save as Draft&quot; when creating an event to see it here.
                    </p>
                    <button
                      className="create-event-btn bg-blue-600 text-white px-4 py-2 rounded-md"
                      onClick={() => openAddEditModal("Add", "")}
                    >
                      Create Event
                    </button>
                  </>
                ) : (
                  <>
                    <p className="empty-events-text mb-4">
                      
                      <br /> Start creating your family event.
                    </p>
                    <button
                      className="create-event-btn bg-blue-600 text-white px-4 py-2 rounded-md"
                      onClick={() => openAddEditModal("Add", "")}
                    >
                      + Create Event
                    </button>
                  </>
                )}
              </div>
            )}
          </>
        )}
        </div>
      </div>
    </div>
  );
};

export default Invitation;
